import { Box, Grid, RadioGroup, Typography, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import _ from 'lodash';
import ColorField from 'components/elements/ColorField';
import ExpRadio from 'components/elements/ExpRadio';
import FanbandTerminal from 'components/elements/FanbandTerminal';
import {
  HeaderText,
  SubHeaderText,
  PrettoSlider,
} from 'components/styled-components';
import {
  LED_TYPES,
  PRESET_PATTERNS,
  VIB_INTENSITIES,
  VIB_TYPES,
} from 'utils/constants';
import { PARAMS_MODES, VIBRATION_MARKS } from './constants';

const useStyles = makeStyles((theme) => ({
  notice: {
    fontFamily: theme.custom.fonts.SFUITextRegular,
    fontSize: 14,
    color: '#44444f',
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2),
    borderLeft: `solid 3px ${theme.palette.info.main}`,
    background: '#f5f5f8',
    borderRadius: 4,
  },
  presetPanel: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    width: '100%',
    marginBottom: theme.spacing(1),
  },
  presetButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    marginRight: 8,
    marginBottom: 8,
    color: '#c8c8c8',
    fontSize: 14,
    fontFamily: theme.custom.fonts.SFUITextMedium,
    border: 'solid 1px #c8c8c8',
    borderRadius: 12,
    cursor: 'pointer',
    '&:hover': {
      border: 'solid 2px #a2a2a2',
      color: '#a2a2a2',
    },
  },
  presetEdited: {
    borderColor: theme.palette.info.main,
    color: theme.palette.info.main,
  },
  presetSelected: {
    background: theme.palette.primary.main,
    color: 'white !important',
    border: 'none !important',
  },
}));

const COLOR_FIELDS = [
  { name: 'topColor1', label: 'Top light #1' },
  { name: 'topColor2', label: 'Top light #2' },
  { name: 'topColor3', label: 'Top light #3' },
  { name: 'bottomColor1', label: 'Bottom light #1' },
  { name: 'bottomColor2', label: 'Bottom light #2' },
  { name: 'bottomColor3', label: 'Bottom light #3' },
];

const ModeNotice = ({ mode, slot }) =>
  mode === PARAMS_MODES.preset ? (
    <>
      This alert plays preset <b>{slot}</b> on both iOS and Android bands.
      Changing the settings below edits preset {slot} itself: once saved, the
      slot is updated on every band and for every alert that uses it.
    </>
  ) : (
    <>
      The settings below apply to this alert only and are played by{' '}
      <b>Android bands only</b>. iOS bands can only play a saved preset, so
      they will play preset <b>{slot}</b> instead. Pick the slot closest to
      these settings.
    </>
  );

const AlertParamsEditor = ({
  params,
  onChange,
  terminalScreen,
  presets,
  mode,
  onModeChange,
  selectedPreset,
  onSelectPreset,
}) => {
  const classes = useStyles();

  const handleDurationChange = (e, value) => {
    onChange({ target: { value, name: 'duration', type: 'slider' } });
  };

  return (
    <Grid container>
      <Grid item lg={9} xs={12}>
        {!!presets && (
          <>
            <HeaderText>Pattern</HeaderText>
            <RadioGroup
              row
              name="mode"
              value={mode}
              onChange={(e) => onModeChange(e.target.value)}
            >
              <ExpRadio
                label="Preset slot (iOS & Android)"
                color="info"
                value={PARAMS_MODES.preset}
              />
              <ExpRadio
                label="Custom for this alert (Android only)"
                color="info"
                value={PARAMS_MODES.custom}
              />
            </RadioGroup>
            <Typography component="div" className={classes.notice}>
              <ModeNotice mode={mode} slot={selectedPreset} />
            </Typography>
            <SubHeaderText>
              {mode === PARAMS_MODES.preset
                ? 'Preset slot'
                : 'Fallback preset slot'}
            </SubHeaderText>
            <div className={classes.presetPanel}>
              {presets.map((preset) => {
                const idx = preset.presetPatternIndex;
                const edited = !_.isEqual(preset, PRESET_PATTERNS[idx]);

                return (
                  <div
                    key={idx}
                    title={
                      edited
                        ? `Preset ${idx} (edited from default)`
                        : `Preset ${idx}`
                    }
                    className={clsx(classes.presetButton, {
                      [classes.presetEdited]: edited,
                      [classes.presetSelected]: selectedPreset === idx,
                    })}
                    onClick={() => onSelectPreset(idx)}
                  >
                    {idx}
                  </div>
                );
              })}
            </div>
          </>
        )}
        <SubHeaderText>Duration</SubHeaderText>
        <Grid container spacing={2}>
          <Grid item lg={8} xs={12}>
            <PrettoSlider
              name="duration"
              valueLabelDisplay="on"
              min={0}
              max={20}
              step={1}
              valueLabelFormat={(value) => `${value}s`}
              marks={VIBRATION_MARKS}
              value={params.duration}
              onChange={handleDurationChange}
            />
          </Grid>
        </Grid>

        <HeaderText>Led Lights</HeaderText>
        <Box mb={2}>
          <RadioGroup row name="ledType" value={params.ledType} onChange={onChange}>
            <ExpRadio label="Flashing" color="info" value={LED_TYPES.flashing} />
            <ExpRadio label="Stable" color="info" value={LED_TYPES.stable} />
            <ExpRadio label="Disabled" color="info" value={LED_TYPES.none} />
          </RadioGroup>
        </Box>
        <SubHeaderText>Color palette</SubHeaderText>
        <Grid container spacing={2}>
          {COLOR_FIELDS.map(({ name, label }) => (
            <Grid item lg={4} xs={6} key={name}>
              <ColorField
                name={name}
                label={label}
                value={params[name]}
                onChange={onChange}
              />
            </Grid>
          ))}
        </Grid>

        <HeaderText>Vibration</HeaderText>

        <SubHeaderText>Intensity</SubHeaderText>
        <Box mb={2}>
          <RadioGroup
            row
            name="vibrationIntensity"
            value={params.vibrationIntensity}
            onChange={onChange}
          >
            <ExpRadio label="Low" color="info" value={VIB_INTENSITIES.low} />
            <ExpRadio label="Medium" color="info" value={VIB_INTENSITIES.medium} />
            <ExpRadio label="High" color="info" value={VIB_INTENSITIES.high} />
            <ExpRadio label="Disabled" color="info" value={VIB_INTENSITIES.none} />
          </RadioGroup>
        </Box>

        <SubHeaderText>Style</SubHeaderText>
        <Box mb={2}>
          <RadioGroup
            row
            name="vibrationType"
            value={params.vibrationType}
            onChange={onChange}
          >
            <ExpRadio label="Quick bursts" color="info" value={VIB_TYPES.quickBursts} />
            <ExpRadio label="Long vibrate" color="info" value={VIB_TYPES.longVibrate} />
            <ExpRadio label="Disabled" color="info" value={VIB_TYPES.none} />
          </RadioGroup>
        </Box>
      </Grid>
      <Grid item lg={3} xs={12} container justifyContent="center">
        <FanbandTerminal params={params}>{terminalScreen}</FanbandTerminal>
      </Grid>
    </Grid>
  );
};

export default AlertParamsEditor;
